/**
 * Die Felder einer Entität — Grundlage für „Add" in der Feldliste einer View und für die
 * Prüfung von „Default Sort Query" und „Default Filter Query" (REQ-29 §2.2).
 *
 * Der Altclient las sie per Reflexion aus den C#-Typen
 * (`IwmAdminDialogAuswahlFields.razor`). Im Browser gibt es keine Reflexion; die Tabellen
 * unter `src/types/fieldTypes/` sind der Ersatz. Sie enthalten die geerbten Felder aus
 * `BaseEntity` am Ende — die Reflexion lieferte sie ebenfalls.
 */

import { getCaption, getFieldNames } from '@/types/captions';
import type { EntityTypes } from '@/types/enums';
import { getFieldValueType, type ColumnValueType } from '@/types/fieldTypes';

export interface EntityFieldInfo {
  name: string;
  /** Beschriftung aus `@/types/captions`; ohne Eintrag der Feldname selbst. */
  caption: string;
  valueType: ColumnValueType;
}

const cache = new Map<EntityTypes, readonly EntityFieldInfo[]>();

/**
 * Alle Felder einer Entität, alphabetisch nach Feldname. Für Entitäten ohne Feldtabelle
 * (Systementitäten wie `ChinaAppSysGroup`) ist die Liste leer — über deren Feldnamen lässt
 * sich dann nichts sagen.
 */
export function entityFields(entity: EntityTypes): readonly EntityFieldInfo[] {
  const cached = cache.get(entity);
  if (cached !== undefined) return cached;

  const result = getFieldNames(entity)
    .map((name) => ({
      name,
      caption: getCaption(entity, name),
      valueType: getFieldValueType(entity, name),
    }))
    .sort((left, right) => left.name.localeCompare(right.name, 'de-DE'));

  cache.set(entity, result);
  return result;
}

/** Liegt zu dieser Entität überhaupt eine Feldliste vor? */
export function hasEntityFields(entity: EntityTypes): boolean {
  return entityFields(entity).length > 0;
}

/** Sucht ohne Rücksicht auf Groß- und Kleinschreibung — wie der Auswahldialog des Altclients. */
export function findEntityField(entity: EntityTypes, name: string): EntityFieldInfo | undefined {
  const needle = name.toLowerCase();
  return entityFields(entity).find((field) => field.name.toLowerCase() === needle);
}
